import React, { useMemo, useState, useEffect } from "react";
import { createPortal } from "react-dom";

import "../styles/PalmsSpecificationAccordion.css";
import { specificationData } from "../data/palmsspecificationData";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, PaintRoller, DoorOpen, Layers, Grid3X3, Utensils,
  Droplet, Zap, Wind, Router, ArrowUpDown, Recycle, BatteryCharging,
  ShieldCheck, Trees, Flower2, Armchair, Hammer, Receipt, Waves, X, ChevronLeft, ChevronRight, CheckCircle2
} from "lucide-react";

/* Icon Map */
const iconMap = {
  structure: <Building2 size={32} />,
  plastering: <Hammer size={32} />,
  painting_cladding: <PaintRoller size={32} />,
  doors_windows_railings: <DoorOpen size={32} />,
  flooring: <Layers size={32} />,
  dadoing: <Grid3X3 size={32} />,
  kitchen: <Utensils size={32} />,
  cp_sanitary: <Droplet size={32} />,
  electrical: <Zap size={32} />,
  air_conditioning: <Wind size={32} />,
  telecom: <Router size={32} />,
  lifts: <ArrowUpDown size={32} />,
  wsp_stp: <Recycle size={32} />,
  ev_charging: <BatteryCharging size={32} />,
  power_backup: <BatteryCharging size={32} />,
  security_bms: <ShieldCheck size={32} />,
  main_landscaping_works: <Trees size={32} />,
  other_landscape_amenities: <Flower2 size={32} />,
  clubhouse_amenities: <Armchair size={32} />,
  centralized_billing: <Receipt size={32} />,
  centralized_water_supply: <Waves size={32} />,
};

const mobileCategories = ["Structure", "Interiors", "Services", "Amenities"];

const categoryById = {
  structure: "Structure",
  plastering: "Interiors",
  painting_cladding: "Interiors",
  doors_windows_railings: "Interiors",
  flooring: "Interiors",
  dadoing: "Interiors",
  kitchen: "Interiors",
  cp_sanitary: "Interiors",
  electrical: "Services",
  air_conditioning: "Services",
  telecom: "Services",
  lifts: "Services",
  wsp_stp: "Services",
  ev_charging: "Services",
  power_backup: "Services",
  security_bms: "Services",
  centralized_billing: "Services",
  centralized_water_supply: "Services",
  main_landscaping_works: "Amenities",
  other_landscape_amenities: "Amenities",
  clubhouse_amenities: "Amenities",
};

function getItemCategory(item) {
  return categoryById[item.id] || "Amenities";
}

function getItemSearchText(item) {
  const pointsText = item.points
    .map((point) => (typeof point === "string" ? point : point.text || ""))
    .join(" ");
  return `${item.title} ${pointsText}`.toLowerCase();
}

export default function SpecificationAccordion() {
  const [selectedId, setSelectedId] = useState(null);
  const [originId, setOriginId] = useState(null);
  const [direction, setDirection] = useState(0);
  const [activeCategory, setActiveCategory] = useState("Amenities");
  const [searchTerm, setSearchTerm] = useState("");

  // Lock body scroll when modal is open
  useEffect(() => {
    if (selectedId) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [selectedId]);

  const mobileData = useMemo(() => {
    const normalized = searchTerm.trim().toLowerCase();
    return specificationData.filter((item) => {
      if (getItemCategory(item) !== activeCategory) return false;
      if (!normalized) return true;
      return getItemSearchText(item).includes(normalized);
    });
  }, [activeCategory, searchTerm]);

  if (!specificationData || specificationData.length === 0) return null;

  // Helpers for nav within modal
  const selectedIndex = specificationData.findIndex((item) => item.id === selectedId);
  const selectedItem = specificationData[selectedIndex];

  const handleTileClick = (id) => {
    setOriginId(id);
    setSelectedId(id);
    setDirection(0);
  };

  const handleNext = (e) => {
    e.stopPropagation();
    setDirection(1);
    const newIndex = (selectedIndex + 1) % specificationData.length;
    setSelectedId(specificationData[newIndex].id);
  };

  const handlePrev = (e) => {
    e.stopPropagation();
    setDirection(-1);
    const newIndex = (selectedIndex - 1 + specificationData.length) % specificationData.length;
    setSelectedId(specificationData[newIndex].id);
  };

  const slideVariants = {
    enter: (dir) => ({
      x: dir > 0 ? 200 : -200,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir) => ({
      x: dir < 0 ? 200 : -200,
      opacity: 0,
    }),
  };

  return (
    <section className="specification-section spec-wrapper py-5" id="serene-specifications">
      <div className="container">
        <div className="d-none d-lg-block">
          {/* HEADER */}
          <div className="spec-matrix-header">
            <motion.h2
              className="spec-main-title"
              initial={{ opacity: 0, y: -20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Technical Specifications
            </motion.h2>
            <motion.p
              className="spec-subtitle"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Click on any tile below to explore the premium construction details.
            </motion.p>
          </div>

          {/* MATRIX GRID */}
          <div className="spec-matrix-grid">
            {specificationData.map((item) => {
              const Icon = iconMap[item.id] || <Building2 size={32} />;

              return (
                <motion.div
                  layoutId={`card-${item.id}`}
                  key={item.id}
                  className="spec-matrix-tile"
                  onClick={() => handleTileClick(item.id)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <div className="spec-tile-icon">
                    {React.cloneElement(Icon, { size: 40 })}
                  </div>
                  <h4 className="spec-tile-title">{item.title}</h4>
                  <div className="spec-tile-shine"></div>
                </motion.div>
              );
            })}
          </div>

          {/* MODAL OVERLAY - PORTALED TO BODY */}
          {createPortal(
            <AnimatePresence>
              {selectedId && selectedItem && (
                <div className="spec-modal-overlay" onClick={() => setSelectedId(null)}>
                  <motion.div
                    layoutId={`card-${originId}`}
                    className="spec-modal-card"
                    onClick={(e) => e.stopPropagation()}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  >
                    <button className="spec-modal-close" onClick={() => setSelectedId(null)}>
                      <X size={20} />
                    </button>

                    <div className="spec-modal-inner-wrapper">
                      <AnimatePresence mode="popLayout" custom={direction} initial={false}>
                        <motion.div
                          key={selectedId}
                          custom={direction}
                          variants={slideVariants}
                          initial="enter"
                          animate="center"
                          exit="exit"
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="spec-modal-motion-div"
                        >
                          <div className="spec-modal-header">
                            <motion.div className="spec-modal-icon-box">
                              {React.cloneElement(iconMap[selectedId] || <Building2 />, { size: 32 })}
                            </motion.div>
                            <div>
                              <span style={{ color: "#94a3b8", fontSize: "0.85rem", textTransform: "uppercase" }}>
                                Specification Details
                              </span>
                              <h3 className="spec-modal-title">{selectedItem.title}</h3>
                            </div>
                          </div>

                          <div className="spec-modal-body" style={{ flex: 1 }}>
                            {selectedItem.points.map((p, i) => {
                              if (typeof p === "object" && p.type === "sub") {
                                return (
                                  <div
                                    key={i}
                                    style={{
                                      color: "#38bdf8",
                                      fontWeight: 600,
                                      marginTop: "15px",
                                      marginBottom: "5px",
                                      textTransform: "uppercase",
                                      letterSpacing: "1px",
                                      fontSize: "0.9rem",
                                    }}
                                  >
                                    {p.text}
                                  </div>
                                );
                              }
                              const text = typeof p === "string" ? p : p.text;
                              return (
                                <div className="spec-modal-row" key={i}>
                                  <CheckCircle2 size={18} style={{ color: "#38bdf8", minWidth: "18px", marginTop: "4px" }} />
                                  <span>{text}</span>
                                </div>
                              );
                            })}
                          </div>

                          <div className="spec-modal-footer">
                            <button className="spec-modal-nav-btn" onClick={handlePrev}>
                              <ChevronLeft size={16} /> Previous
                            </button>
                            <span style={{ color: "#64748b", fontSize: "0.9rem" }}>
                              {selectedIndex + 1} / {specificationData.length}
                            </span>
                            <button className="spec-modal-nav-btn" onClick={handleNext}>
                              Next <ChevronRight size={16} />
                            </button>
                          </div>
                        </motion.div>
                      </AnimatePresence>
                    </div>
                  </motion.div>
                </div>
              )}
            </AnimatePresence>,
            document.body
          )}
        </div>

        <div className="spec-mobile palms-spec-mobile d-lg-none">
          <div className="spec-mobile-head">
            <h3 className="spec-mobile-title">Specifications</h3>
          </div>

          <div className="spec-mobile-tabs" role="tablist" aria-label="Specification categories">
            {mobileCategories.map((category) => (
              <button
                key={category}
                type="button"
                className={`spec-mobile-tab ${activeCategory === category ? "active" : ""}`}
                onClick={() => setActiveCategory(category)}
              >
                {category}
              </button>
            ))}
          </div>

          <div className="spec-mobile-search-wrap">
            <span className="spec-mobile-search-icon" aria-hidden="true">{"\u2315"}</span>
            <input
              type="search"
              className="spec-mobile-search"
              placeholder="Search specifications..."
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              aria-label="Search specifications"
            />
          </div>

          <div className="spec-mobile-section-title">
            {activeCategory} Specifications &middot; {mobileData.length}
          </div>

          <div className="accordion palms-spec-mobile-accordion" id="palmsSpecAccordionMobile">
            {mobileData.map((item) => {
              const collapseId = `palms_mobile_collapse_${item.id}`;
              const headingId = `palms_mobile_heading_${item.id}`;

              return (
                <div className="accordion-item palms-spec-mobile-item mb-2" key={item.id}>
                  <h2 className="accordion-header" id={headingId}>
                    <button
                      className="accordion-button palms-spec-mobile-btn collapsed"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#${collapseId}`}
                      aria-expanded="false"
                      aria-controls={collapseId}
                    >
                      <div className="d-flex align-items-center gap-2 w-100">
                        <div className="palms-spec-mobile-emoji">
                          {React.cloneElement(iconMap[item.id] || <Building2 />, { size: 20 })}
                        </div>
                        <span className="palms-spec-mobile-item-title">{item.title}</span>
                      </div>
                      <span className="palms-spec-mobile-arrow">
                        <span className="arrow">^</span>
                      </span>
                    </button>
                  </h2>

                  <div
                    id={collapseId}
                    className="accordion-collapse collapse"
                    aria-labelledby={headingId}
                    data-bs-parent="#palmsSpecAccordionMobile"
                  >
                    <div className="accordion-body palms-spec-mobile-body">
                      {item.points.map((p, i) => {
                        if (typeof p === "object" && p.type === "sub") {
                          return <div className="palms-spec-mobile-row fw-semibold" key={i}>{p.text}</div>;
                        }
                        const text = typeof p === "string" ? p : p.text;
                        return <div className="palms-spec-mobile-row" key={i}>{text}</div>;
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {mobileData.length === 0 && (
            <div className="palms-spec-mobile-empty">No specifications match your search.</div>
          )}
        </div>
      </div>
    </section>
  );
}
